// Заглавие на колона в справка: етикетът (сортира при клик) + фуния, която
// отваря филтъра на колоната точно под нея. Активен филтър: етикетът става
// кехлибарен със значка за броя избрани, фунията е плътна. Числовите колони
// са подравнени вдясно — там фунията стои ОТЛЯВО на етикета, за да остане
// етикетът над числата.
//
// Попъпът е antd Popover → общият пазач за отворен слой (hasOpenOverlay) го
// вижда. Клавиатура: Enter/Space/↓ върху фунията отваря, Esc затваря и връща
// фокуса на фунията (всеки Esc — с preventDefault); вътрешността е в
// `ColumnFilterPanel`.
//
// `FiltersButton` е същият попъп с всички секции наведнъж — за телефона, където
// таблицата е карти и заглавия няма.
import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Popover } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  FilterFilled,
  FilterOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ColumnFilterPanel } from "./ColumnFilterPanel";
import { selectedCountOf, type ColumnFilterSection } from "./useColumnFilters";

export type SortDirection = "asc" | "desc";

// Заглавията на колоните в таблиците на справките (едни и същи навсякъде)
export const COLUMN_HEAD_TEXT_CLASS =
  "text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em]";

// ─── Попъпът и неговият бутон ─────────────────────────────────────────────
type MenuProps = {
  sections: ColumnFilterSection[];
  placement: "bottomLeft" | "bottomRight";
  renderTrigger: (args: {
    open: boolean;
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
    buttonRef: (element: HTMLButtonElement | null) => void;
  }) => ReactNode;
};

const FilterMenu = ({ sections, placement, renderTrigger }: MenuProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      close(true);
    } else if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement={placement}
      arrow={false}
      destroyOnHidden
      classNames={{ body: "!p-0" }}
      content={<ColumnFilterPanel sections={sections} onClose={close} />}
    >
      {renderTrigger({
        open,
        onKeyDown,
        buttonRef: (element) => {
          triggerRef.current = element;
        },
      })}
    </Popover>
  );
};

// Значката с броя избрани стойности
const CountBadge = ({ count }: { count: number }) => (
  <span className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-brand px-1 font-sheet text-[10px] font-semibold normal-case leading-none tracking-normal text-white">
    {count}
  </span>
);

// ─── Заглавието на колона ─────────────────────────────────────────────────
type Props = {
  label: string;
  align?: "left" | "right";
  // Сортиране по колоната: посоката, ако сортира тя (иначе null), и кликът
  sortDir?: SortDirection | null;
  onSort?: () => void;
  // Секциите на филтъра; без тях колоната няма фуния
  filters?: ColumnFilterSection[];
  // Дълъг етикет в тясна колона: на два реда вместо „…"
  wrap?: boolean;
};

export const ColumnHeader = ({ label, align = "left", sortDir = null, onSort, filters, wrap = false }: Props) => {
  const { t } = useTranslation();
  const listCount = (filters ?? []).reduce(
    (sum, section) => sum + (section.kind === "list" ? section.selected.length : 0),
    0,
  );
  const filtered = filters ? selectedCountOf(filters) > 0 : false;
  const right = align === "right";
  const tone = filtered || sortDir ? "text-brand-deep-ink" : "text-muted";

  const labelContent = (
    <>
      {wrap ? (
        <span className={`min-w-0 break-words ${right ? "text-right" : "text-left"}`}>{label}</span>
      ) : (
        <span className="truncate">{label}</span>
      )}
      {listCount > 0 && <CountBadge count={listCount} />}
      {sortDir === "asc" && <ArrowUpOutlined className="text-[10px]" />}
      {sortDir === "desc" && <ArrowDownOutlined className="text-[10px]" />}
    </>
  );
  const labelClass = `flex min-w-0 items-center gap-1 ${COLUMN_HEAD_TEXT_CLASS} ${tone}`;

  // `wrap` → max-w-full: етикетът не излиза в съседната колона, а се пренася
  // (justify-self свива клетката до съдържанието — без таван то се разлива)
  return (
    <div
      className={`flex min-w-0 items-center gap-1 ${wrap ? "max-w-full" : ""} ${
        right ? "flex-row-reverse justify-self-end" : "justify-self-start"
      }`}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          aria-sort={sortDir === "asc" ? "ascending" : sortDir === "desc" ? "descending" : undefined}
          className={`${labelClass} cursor-pointer rounded border-none bg-transparent p-0 ${
            wrap && right ? "text-right" : "text-left"
          } outline-none hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--color-brand-tint)]`}
        >
          {labelContent}
        </button>
      ) : (
        <span className={labelClass}>{labelContent}</span>
      )}
      {filters && filters.length > 0 && (
        <FilterMenu
          sections={filters}
          placement={right ? "bottomRight" : "bottomLeft"}
          renderTrigger={({ open, onKeyDown, buttonRef }) => (
            <button
              ref={buttonRef}
              type="button"
              aria-label={t("inventoryPage.cfFilterAria", { column: label })}
              aria-haspopup="dialog"
              aria-expanded={open}
              onKeyDown={onKeyDown}
              className={`flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-[5px] border-none p-0 outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)] ${
                filtered
                  ? "bg-brand-tint text-brand-deep-ink"
                  : open
                    ? "bg-shade-2 text-ink"
                    : "bg-transparent text-faint hover:bg-shade-1 hover:text-ink"
              }`}
            >
              {filtered ? <FilterFilled className="text-[11px]" /> : <FilterOutlined className="text-[11px]" />}
            </button>
          )}
        />
      )}
    </div>
  );
};

// ─── „Филтри" на телефон (таблицата е карти — заглавия няма) ──────────────
export const FiltersButton = ({ sections }: { sections: ColumnFilterSection[] }) => {
  const { t } = useTranslation();
  const count = selectedCountOf(sections);
  if (!sections.length) return null;
  return (
    <FilterMenu
      sections={sections}
      placement="bottomRight"
      renderTrigger={({ open, onKeyDown, buttonRef }) => (
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onKeyDown={onKeyDown}
          className={`flex shrink-0 cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-[9px] border px-3 py-2 text-[13.5px] leading-[normal] text-ink outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
            count ? "border-brand/45 bg-brand-tint" : "border-shade-3 bg-surface"
          }`}
        >
          {count ? <FilterFilled className="text-xs text-brand-deep-ink" /> : <FilterOutlined className="text-xs text-faint" />}
          {t("inventoryPage.cfFilters")}
          {count > 0 && <CountBadge count={count} />}
        </button>
      )}
    />
  );
};

export default ColumnHeader;
